import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';
import { SeedTree, type RandomStream } from '../../mod-035/src/index.js';

export interface MicroEventCloudOptions {
  readonly sampleRate: number;
  readonly seed?: number;
  readonly densityHz?: number;
  readonly cluster?: number;
  readonly jitter?: number;
  readonly maxEventsPerBlock?: number;
}

export interface MicroEventOutputs {
  readonly trigger: Float32Array;
  readonly count: Float32Array;
  readonly amplitude: Float32Array;
  readonly pan: Float32Array;
}

interface PendingEvent {
  readonly frame: number;
  readonly amplitude: number;
  readonly pan: number;
  readonly order: number;
}

const UINT32_MAX = 0xffff_ffff;
const MAX_DENSITY_HZ = 1_000;
const MAX_PENDING_EVENTS = 256;
const MAX_CLUSTER_CHILDREN = 7;
const MAX_CLUSTER_WINDOW_SECONDS = 0.02;

const assertFiniteRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

const assertIntegerRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be an integer in [${minimum}, ${maximum}]`);
  }
};

const exponentialThreshold = (stream: RandomStream): number => -Math.log(1 - stream.next());

/**
 * Deterministic sample-frame point process. The host owns buffers, sources and
 * routing; this class emits only bounded event controls.
 */
export class MicroEventCloud {
  readonly sampleRate: number;
  readonly maxEventsPerBlock: number;

  private seed: number;
  private densityHz: number;
  private cluster: number;
  private jitter: number;
  private timing!: RandomStream;
  private clusterLayout!: RandomStream;
  private amplitudes!: RandomStream;
  private pans!: RandomStream;
  private hazard = 0;
  private nextThreshold = 0;
  private frame = 0;
  private nextOrder = 0;
  private readonly pending: PendingEvent[] = [];

  constructor(options: MicroEventCloudOptions) {
    assertFiniteRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.seed = options.seed ?? 0;
    this.densityHz = options.densityHz ?? 20;
    this.cluster = options.cluster ?? 0;
    this.jitter = options.jitter ?? 0.5;
    this.maxEventsPerBlock = options.maxEventsPerBlock ?? 256;
    this.validate();
    this.reseed();
  }

  reset(): void { this.reseed(); }

  setParameter(id: string, value: number): void {
    if (id === 'density-hz') { assertFiniteRange(id, value, 0, MAX_DENSITY_HZ); this.densityHz = value; return; }
    if (id === 'cluster') { assertFiniteRange(id, value, 0, 1); this.cluster = value; return; }
    if (id === 'jitter') { assertFiniteRange(id, value, 0, 1); this.jitter = value; return; }
    if (id === 'seed') {
      assertIntegerRange(id, value, 0, UINT32_MAX);
      this.seed = value;
      this.reseed();
      return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(outputs: MicroEventOutputs, frames = outputs.trigger.length, densityEnvelope?: Float32Array): void {
    if (!Number.isInteger(frames) || frames < 0) throw new RangeError('frames must be a non-negative integer');
    for (const buffer of [outputs.trigger, outputs.count, outputs.amplitude, outputs.pan]) {
      if (!(buffer instanceof Float32Array) || buffer.length < frames) throw new RangeError('output buffer is missing or too short');
    }
    if (densityEnvelope !== undefined && (!(densityEnvelope instanceof Float32Array) || densityEnvelope.length < frames)) {
      throw new RangeError('density envelope buffer is missing or too short');
    }
    if (densityEnvelope !== undefined) {
      for (let index = 0; index < frames; index += 1) {
        assertFiniteRange('density envelope', densityEnvelope[index]!, 0, 1);
      }
    }

    let acceptedEvents = 0;
    for (let index = 0; index < frames; index += 1) {
      outputs.trigger[index] = 0;
      outputs.count[index] = 0;
      outputs.amplitude[index] = 0;
      outputs.pan[index] = 0;

      const envelope = densityEnvelope?.[index] ?? 1;
      this.hazard += this.densityHz * envelope / this.sampleRate;
      while (this.hazard >= this.nextThreshold) {
        this.nextThreshold += exponentialThreshold(this.timing);
        acceptedEvents = this.emitAt(outputs, index, acceptedEvents, this.amplitudes.uniform(0.25, 1), this.pans.uniform(-1, 1));
        this.scheduleCluster();
      }
      acceptedEvents = this.emitPending(outputs, index, acceptedEvents);
      this.frame += 1;
    }
  }

  private validate(): void {
    assertIntegerRange('seed', this.seed, 0, UINT32_MAX);
    assertFiniteRange('densityHz', this.densityHz, 0, MAX_DENSITY_HZ);
    assertFiniteRange('cluster', this.cluster, 0, 1);
    assertFiniteRange('jitter', this.jitter, 0, 1);
    assertIntegerRange('maxEventsPerBlock', this.maxEventsPerBlock, 1, 256);
  }

  private reseed(): void {
    const tree = new SeedTree(this.seed).child('mod-006');
    this.timing = tree.stream('timing');
    this.clusterLayout = tree.stream('cluster-layout');
    this.amplitudes = tree.stream('amplitude');
    this.pans = tree.stream('pan');
    this.hazard = 0;
    this.nextThreshold = exponentialThreshold(this.timing);
    this.frame = 0;
    this.nextOrder = 0;
    this.pending.length = 0;
  }

  private scheduleCluster(): void {
    if (this.cluster <= 0 || this.clusterLayout.next() >= this.cluster) return;
    const children = 1 + Math.floor(this.clusterLayout.next() * MAX_CLUSTER_CHILDREN * this.cluster);
    const windowFrames = Math.floor(this.sampleRate * MAX_CLUSTER_WINDOW_SECONDS * this.jitter);
    for (let child = 0; child < children; child += 1) {
      if (this.pending.length >= MAX_PENDING_EVENTS) throw new RangeError(`pending cloud exceeds ${MAX_PENDING_EVENTS} events`);
      const offset = child === 0 ? 0 : Math.floor(this.clusterLayout.next() * (windowFrames + 1));
      this.pending.push({
        frame: this.frame + offset,
        amplitude: this.amplitudes.uniform(0.1, 0.85),
        pan: this.pans.uniform(-1, 1),
        order: this.nextOrder++,
      });
    }
    this.pending.sort((left, right) => left.frame - right.frame || left.order - right.order);
  }

  private emitPending(outputs: MicroEventOutputs, index: number, acceptedEvents: number): number {
    let read = 0;
    while (read < this.pending.length && this.pending[read]!.frame <= this.frame) {
      const event = this.pending[read++]!;
      acceptedEvents = this.emitAt(outputs, index, acceptedEvents, event.amplitude, event.pan);
    }
    if (read > 0) this.pending.splice(0, read);
    return acceptedEvents;
  }

  private emitAt(
    outputs: MicroEventOutputs,
    index: number,
    acceptedEvents: number,
    amplitude: number,
    pan: number,
  ): number {
    const nextAccepted = acceptedEvents + 1;
    if (nextAccepted > this.maxEventsPerBlock) throw new RangeError(`block exceeds ${this.maxEventsPerBlock} events`);
    const previousCount = outputs.count[index]!;
    outputs.trigger[index] = 1;
    outputs.count[index] = previousCount + 1;
    outputs.amplitude[index] = Math.max(outputs.amplitude[index]!, amplitude);
    outputs.pan[index] = (outputs.pan[index]! * previousCount + pan) / (previousCount + 1);
    return nextAccepted;
  }
}

class MicroEventModuleInstance implements DspModuleInstance {
  private readonly cloud: MicroEventCloud;

  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.cloud = new MicroEventCloud({ sampleRate });
  }

  reset(): void { this.cloud.reset(); }
  setParameter(id: string, value: number): void { this.cloud.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const trigger = block.outputs.trigger;
    const count = block.outputs.count;
    const amplitude = block.outputs.amplitude;
    const pan = block.outputs.pan;
    if (!trigger || !count || !amplitude || !pan) throw new RangeError('micro-event output buffer is missing');
    this.cloud.processInto({ trigger, count, amplitude, pan }, block.frames, block.controls['density-envelope']);
  }
}

export const mod006 = defineDspModule({
  descriptor: {
    id: 'mir.mod-006',
    name: 'Micro-event / grain cloud generator',
    version: '0.1.0',
    inputs: [{ id: 'density-envelope', kind: 'control', channels: 1 }],
    outputs: [
      { id: 'trigger', kind: 'control', channels: 1 },
      { id: 'count', kind: 'control', channels: 1 },
      { id: 'amplitude', kind: 'control', channels: 1 },
      { id: 'pan', kind: 'control', channels: 1 },
    ],
    parameters: [
      { id: 'density-hz', defaultValue: 20, minValue: 0, maxValue: MAX_DENSITY_HZ, unit: 'Hz' },
      { id: 'cluster', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'jitter', defaultValue: 0.5, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'seed', defaultValue: 0, minValue: 0, maxValue: UINT32_MAX, unit: 'uint32' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new MicroEventModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
